import React, { useLayoutEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/auth';
import Loading from '../layouts/loading';
import MainLayout from '../layouts/main';
import GenericError from '../pages/Errors/GenericError';

interface IRouteProps {
  isPrivate?: boolean;
  component: React.ComponentType;
  customLayout?: boolean;
}

export const COMPLETE_PROFILE_PATH = '/complete-profile';

/*
 * `profileCompleted` tem TRÊS estados (true / false / undefined) e as
 * comparações abaixo são sempre estritas de propósito:
 *
 *   PrivateRoute empurra para a tela de conclusão só em `=== false`;
 *   ProfileCompletionRoute empurra para fora dela só em `=== true`.
 *
 * `undefined` (bootstrap falhou) não satisfaz nenhum dos dois, então o estado
 * desconhecido cai na página de erro com retry em vez de ficar em ping-pong.
 * Trocar qualquer uma dessas comparações por truthiness reintroduz o loop.
 */

// Redireciona para /home se o usuário já estiver autenticado
export const GuestRoute: React.FC<{ component: React.ComponentType }> = ({
  component: Component,
}) => {
  const { isAuthenticated, profileCompleted, loading } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return (
      <Navigate
        to={profileCompleted === false ? COMPLETE_PROFILE_PATH : '/home'}
        replace
      />
    );
  }

  return <Component />;
};

/**
 * Tela de conclusão de cadastro. Renderiza sem o MainLayout de propósito: com
 * o perfil incompleto o backend bloqueia notificações, gamificação e perfil,
 * então o header/sidebar só produziriam uma rajada de 403.
 */
export const ProfileCompletionRoute: React.FC<{
  component: React.ComponentType;
}> = ({ component: Component }) => {
  const {
    isAuthenticated,
    profileCompleted,
    bootstrapError,
    retryBootstrap,
    loading,
  } = useAuth();

  if (loading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (bootstrapError) {
    return <GenericError onRetry={retryBootstrap} />;
  }

  if (profileCompleted === true) {
    return <Navigate to="/home" replace />;
  }

  return <Component />;
};

const PrivateRoute: React.FC<IRouteProps> = ({
  isPrivate = false,
  customLayout = false,
  component: Component,
}) => {
  const {
    user,
    isAuthenticated,
    profileCompleted,
    bootstrapError,
    retryBootstrap,
    loading,
  } = useAuth();
  const location = useLocation();

  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) {
    return <Loading />;
  }

  // Salva a URL atual (pathname + search + hash) para redirecionar após o login
  const redirectTo = `${location.pathname}${location.search}${location.hash}`;

  if (isPrivate && !isAuthenticated) {
    return <Navigate to="/" state={{ from: redirectTo }} replace />;
  }

  // Falha de rede/5xx no bootstrap: o usuário continua logado e recebe a
  // opção de tentar de novo, em vez de uma tela em branco ou um logout.
  if (isAuthenticated && bootstrapError) {
    return <GenericError onRetry={retryBootstrap} />;
  }

  if (isPrivate && profileCompleted === false) {
    return (
      <Navigate
        to={COMPLETE_PROFILE_PATH}
        state={{ from: redirectTo }}
        replace
      />
    );
  }

  // Perfil concluído mas o IUser ainda não hidratou: várias telas leem
  // `user.*` sem proteção, então segurar aqui evita o crash.
  if (isPrivate && !user) {
    return <Loading />;
  }

  const Layout: React.FC<React.PropsWithChildren> =
    isAuthenticated && !customLayout
      ? MainLayout
      : ({ children }) => <>{children}</>;

  return (
    <>
      {isPrivate === isAuthenticated ? (
        <Layout>
          <Component />
        </Layout>
      ) : (
        <Navigate to="/" replace />
      )}
    </>
  );
};

export default PrivateRoute;
